import React, { useState, useEffect } from "react";
import axios from "axios";
import "./RcaModal.css";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadgeClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "closed" || s === "resolved") return "rca-badge-status-closed";
  if (s === "open" || s === "new") return "rca-badge-status-open";
  if (s === "escalated") return "rca-badge-status-escalated";
  return "rca-badge-status-default";
};

const priorityBadgeClass = (priority) => {
  const p = (priority || "").toLowerCase();
  if (p === "high" || p === "critical") return "rca-badge-priority-high";
  if (p === "medium") return "rca-badge-priority-medium";
  if (p === "low") return "rca-badge-priority-low";
  return "rca-badge-priority-default";
};

const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("### ")) {
      elements.push(<h3 key={i}>{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      elements.push(<h2 key={i}>{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      elements.push(<h1 key={i}>{line.slice(2)}</h1>);
    } else if (line.match(/^[-*] /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*] /)) {
        items.push(lines[i].replace(/^[-*] /, ""));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`}>
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    } else if (line.match(/^\d+\. /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\. /)) {
        items.push(lines[i].replace(/^\d+\. /, ""));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`}>
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    } else if (line.trim() === "") {
      // skip blank lines
    } else {
      elements.push(<p key={i}>{renderInline(line)}</p>);
    }
    i++;
  }
  return elements;
};

const RcaModal = ({ isOpen, onClose, caseNumber, ticketSubject }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (isOpen && caseNumber) {
      setLoading(true);
      setError("");
      setResult(null);

      axios
        .get(`/sfdc/ticket/${encodeURIComponent(caseNumber)}`)
        .then((res) => setResult(res.data))
        .catch((err) => {
          const msg =
            err.response?.data?.error ||
            "Failed to fetch ticket analysis. Please try again.";
          setError(msg);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, caseNumber]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("rca-overlay")) {
      onClose();
    }
  };

  const handleClose = () => {
    setResult(null);
    setError("");
    onClose();
  };

  const ticket = result?.ticket;
  const comments = ticket?.comments || [];

  return (
    <div className="rca-overlay" onClick={handleOverlayClick}>
      <div className="rca-modal">
        <button className="rca-close-btn" onClick={handleClose}>
          &times;
        </button>

        <div className="rca-modal-title">
          RCA — {ticketSubject || `Case #${caseNumber}`}
          {result?.source && (
            <span
              style={{
                marginLeft: 12,
                fontSize: "0.7rem",
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 9999,
                verticalAlign: "middle",
                background: result.source === "database" ? "#c6f6d5" : "#bee3f8",
                color: result.source === "database" ? "#276749" : "#2a4365",
              }}
            >
              {result.source === "database" ? "PRE-COMPUTED" : "LIVE"}
            </span>
          )}
        </div>

        {loading && (
          <div className="rca-loading">
            <div className="rca-spinner" />
            <p>Fetching from Salesforce and running AI analysis...</p>
          </div>
        )}

        {error && <div className="rca-error">{error}</div>}

        {result && ticket && (
          <div className="rca-result-grid">
            {/* Ticket Details */}
            <div className="rca-card">
              <div className="rca-card-title">
                <svg className="rca-card-icon" viewBox="0 0 20 20" fill="#0c4280">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  />
                </svg>
                Ticket Details — #{ticket.case_number}
              </div>

              <div className="rca-meta-grid">
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Subject</span>
                  <span className="rca-meta-value">{ticket.subject || "—"}</span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Status</span>
                  <span className="rca-meta-value">
                    <span className={`rca-badge ${statusBadgeClass(ticket.status)}`}>
                      {ticket.status || "—"}
                    </span>
                  </span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Priority</span>
                  <span className="rca-meta-value">
                    <span className={`rca-badge ${priorityBadgeClass(ticket.priority)}`}>
                      {ticket.priority || "—"}
                    </span>
                  </span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Account</span>
                  <span className="rca-meta-value">{ticket.account_name || "—"}</span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Contact</span>
                  <span className="rca-meta-value">
                    {ticket.contact_name || "—"}
                    {ticket.contact_email ? ` (${ticket.contact_email})` : ""}
                  </span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Owner</span>
                  <span className="rca-meta-value">{ticket.owner || "—"}</span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Created</span>
                  <span className="rca-meta-value">{formatDate(ticket.created_date)}</span>
                </div>
                <div className="rca-meta-item">
                  <span className="rca-meta-label">Closed</span>
                  <span className="rca-meta-value">{formatDate(ticket.closed_date)}</span>
                </div>
                {ticket.origin && (
                  <div className="rca-meta-item">
                    <span className="rca-meta-label">Origin</span>
                    <span className="rca-meta-value">{ticket.origin}</span>
                  </div>
                )}
                {ticket.type && (
                  <div className="rca-meta-item">
                    <span className="rca-meta-label">Type</span>
                    <span className="rca-meta-value">{ticket.type}</span>
                  </div>
                )}
              </div>

              {ticket.description && (
                <div className="rca-description">
                  <div className="rca-description-label">Description</div>
                  <div className="rca-description-text">{ticket.description}</div>
                </div>
              )}
            </div>

            {/* AI Summary */}
            <div className="rca-card">
              <div className="rca-card-title">
                <svg className="rca-card-icon" viewBox="0 0 20 20" fill="#0c4280">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                AI Summary
              </div>
              <div className="rca-ai-content">{renderMarkdown(result.summary)}</div>
            </div>

            {/* Root Cause */}
            <div className="rca-card">
              <div className="rca-card-title">
                <svg className="rca-card-icon" viewBox="0 0 20 20" fill="#c53030">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Root Cause Analysis
              </div>
              <div className="rca-ai-content">{renderMarkdown(result.root_cause)}</div>
            </div>

            {/* Comments Timeline */}
            <div className="rca-card">
              <div className="rca-card-title">
                <svg className="rca-card-icon" viewBox="0 0 20 20" fill="#0c4280">
                  <path
                    fillRule="evenodd"
                    d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2z"
                    clipRule="evenodd"
                  />
                </svg>
                Discussion ({comments.length} comment{comments.length !== 1 ? "s" : ""})
              </div>

              {comments.length === 0 ? (
                <div className="rca-no-comments">No comments found for this ticket.</div>
              ) : (
                <div className="rca-comments-timeline">
                  {comments.map((c, idx) => (
                    <div key={idx} className="rca-comment-item">
                      <div className="rca-comment-dot" />
                      <div className="rca-comment-header">
                        <span className="rca-comment-author">{c.author}</span>
                        <span className="rca-comment-date">{formatDate(c.date)}</span>
                      </div>
                      <div className="rca-comment-body">{c.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RcaModal;
